<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { ArrowLeft, Bot, History, Pause, Play, RefreshCcw, Sparkles, Square, Trash2, Volume2, X } from '@lucide/vue'
import { useRouter } from 'vue-router'
import { api, post } from '../api'
import { experience } from '../state'
import { germanSpeech, speakGerman, stopGermanSpeech } from '../speech'
import { isEnglish } from '../i18n'

const router = useRouter(), tab = ref('generate'), level = ref('A1'), story = ref(null), stories = ref([])
const busy = ref(false), historyBusy = ref(false), error = ref(''), speed = ref(1), speaking = ref(false), paused = ref(false)
const nextCursor = ref(null)
const answeringQuestion = ref(null)
const selectedWordPopup = ref(null)
const l = computed(() => isEnglish.value ? {
  title: 'Put new words into a story', generateRead: 'Generate & read', history: 'History', create: 'Create a story for today',
  description: 'It prioritizes your current chapter and weaker words. Mastered words are excluded by default.', generate: 'Generate story', writing: 'Writing…',
  read: 'Read aloud', resume: 'Resume', pause: 'Pause', stop: 'Stop', translation: 'Show translation',
  comprehension: 'Comprehension practice', questionTip: 'After reading, choose the answer that best matches the story.', correct: 'Correct!', retry: 'Take another look at the story.', answer: 'Correct answer:', evidence: 'Evidence:',
  oldQuestions: 'This older story has no interactive questions. Regenerate it to add practice.', regenerate: 'Regenerate with the same words', newTopic: 'New topic', ask: 'Ask teacher',
  opening: 'Opening your storybook…', empty: 'No stories yet', emptyDetail: 'Your first story will stay here until you delete it.', currentChapter: 'current chapter',
  sameTitle: 'Regenerate with the same words', delete: 'Delete story', loading: 'Loading…', loadMore: 'Load older stories',
  oldTranslation: 'This older story has no English translation. Regenerate it to create one.', generated: 'Just generated',
} : {
  title: '把新词放进一个故事', generateRead: '生成与阅读', history: '历史', create: '生成一篇属于今天的短文',
  description: '优先使用当前章节和薄弱词，已经完全掌握的词默认不会加入。', generate: '生成短文', writing: '正在写作…',
  read: '朗读全文', resume: '继续朗读', pause: '暂停', stop: '停止', translation: '查看翻译',
  comprehension: '理解练习', questionTip: '读完短文后，选择最符合内容的答案。', correct: '答对了！', retry: '再看一下短文。', answer: '正确答案：', evidence: '原文依据：',
  oldQuestions: '这篇旧短文没有可用的理解题；重新生成即可获得互动练习。', regenerate: '使用相同词重新生成', newTopic: '生成新主题', ask: '问老师',
  opening: '正在翻开短文册…', empty: '还没有生成过短文', emptyDetail: '第一篇短文会永久保存在这里，直到你主动删除。', currentChapter: '当前章节',
  sameTitle: '使用相同词重新生成', delete: '删除短文', loading: '加载中…', loadMore: '加载更早的短文', oldTranslation: '', generated: '刚刚生成',
})

function escapeRegex(str) { return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') }
const storySegments = computed(() => {
  if (!story.value?.story_de || !story.value?.words?.length) return null
  const text = story.value.story_de
  const byGerman = {}
  for (const w of story.value.words) { if (w.german && !byGerman[w.german.toLowerCase()]) byGerman[w.german.toLowerCase()] = w }
  const spans = []
  for (const [key, word] of Object.entries(byGerman)) {
    const re = new RegExp(`\\b${escapeRegex(key)}\\b`, 'gi')
    for (const m of text.matchAll(re)) spans.push({ start: m.index, end: m.index + m[0].length, word })
  }
  spans.sort((a, b) => a.start - b.start || b.end - a.end)
  const merged = []; for (const s of spans) { if (!merged.length || s.start >= merged[merged.length-1].end) merged.push(s) }
  const segs = []; let pos = 0
  for (const s of merged) {
    if (s.start > pos) segs.push({ text: text.slice(pos, s.start), word: null })
    segs.push({ text: text.slice(s.start, s.end), word: s.word }); pos = s.end
  }
  if (pos < text.length) segs.push({ text: text.slice(pos), word: null })
  return segs
})
function selectStoryWord(word, event) {
  const rect = event.target.getBoundingClientRect()
  selectedWordPopup.value = { word, x: Math.min(rect.left + rect.width/2, window.innerWidth - 130), y: rect.bottom + 8, top: rect.top }
}
function closeWordPopup() { selectedWordPopup.value = null }
function askAboutWord(word) {
  selectedWordPopup.value = null
  window.dispatchEvent(new CustomEvent('wortgarten-assistant-context', { detail: {
    word_id: word.id, german: word.german, article: word.article, chinese: word.chinese,
    part_of_speech: word.part_of_speech, dimension: `AI 短文 · ${story.value?.level||''}`,
    code: word.course_code, chapter_number: word.chapter_number, answer_revealed: true, action: 'open',
  }}))
}
function onDocClick(e) {
  if (selectedWordPopup.value && !e.target.closest('.story-word-hl') && !e.target.closest('.word-popup')) selectedWordPopup.value = null
}
const speechAvailable = computed(() => germanSpeech.available)
function formatDate(value) { return value ? new Date(value).toLocaleString(isEnglish.value ? 'en-US' : 'zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : l.value.generated }

function stop() { stopGermanSpeech(); speaking.value = false; paused.value = false }
function speak() {
  if (!story.value || !speechAvailable.value) return
  stop()
  speakGerman(story.value.story_de, {
    rate: speed.value,
    onstart: () => { speaking.value = true; paused.value = false },
    onend: () => { speaking.value = false; paused.value = false },
    onerror: () => { speaking.value = false; paused.value = false; error.value = '当前设备暂时无法朗读，短文仍可正常使用。' },
  })
}
function pauseOrResume() {
  if (!speaking.value) return speak()
  if (paused.value) { speechSynthesis.resume(); paused.value = false }
  else { speechSynthesis.pause(); paused.value = true }
}
async function autoplay() { await nextTick(); if (experience.story_autoplay) speak() }
async function generate() {
  busy.value = true; error.value = ''; stop()
  try { story.value = await post('/api/ai/story', { level: level.value }); await autoplay(); await loadHistory(true) }
  catch (e) { error.value = e.message } finally { busy.value = false }
}
async function loadHistory(reset = false) {
  if (historyBusy.value) return
  historyBusy.value = true
  try {
    const cursor = reset ? '' : nextCursor.value ? `&cursor=${nextCursor.value}` : ''
    const data = await api(`/api/ai/stories?limit=20${cursor}`)
    stories.value = reset ? data.stories : [...stories.value, ...data.stories]
    nextCursor.value = data.next_cursor
  } finally { historyBusy.value = false }
}
async function openStory(item) {
  stop(); error.value = ''; story.value = await api(`/api/ai/stories/${item.id}`); level.value = story.value.level; tab.value = 'generate'; await autoplay()
}
async function removeStory(item) {
  if (!window.confirm(`删除短文“${item.title}”？删除后无法恢复。`)) return
  await api(`/api/ai/stories/${item.id}`, { method: 'DELETE' })
  stories.value = stories.value.filter(storyItem => storyItem.id !== item.id)
  if (story.value?.id === item.id) { stop(); story.value = null }
}
async function regenerate(item = story.value) {
  if (!item) return
  busy.value = true; error.value = ''; stop()
  try { story.value = await post(`/api/ai/stories/${item.id}/regenerate`); tab.value = 'generate'; await autoplay(); await loadHistory(true) }
  catch (e) { error.value = e.message } finally { busy.value = false }
}
async function answerQuestion(question, choice) {
  if (!story.value || question.answered || answeringQuestion.value !== null) return
  answeringQuestion.value = question.index
  try {
    question.answered = await post(`/api/ai/stories/${story.value.id}/questions/${question.index}/answer`, { choice })
  } catch (e) { error.value = e.message } finally { answeringQuestion.value = null }
}
function changeSpeed(value) { speed.value = value; if (speaking.value) speak() }
watch(story, (value) => {
  if (!value?.id) return
  window.dispatchEvent(new CustomEvent('wortgarten-assistant-context', { detail: {
    source: 'story', story_id: value.id, title: value.title, level: value.level,
    german: value.story_de?.slice(0, 600), dimension: `AI 短文 · ${value.level}`,
  }}))
})
onMounted(async () => {
  const path = await api('/api/settings/learning-path').catch(() => null)
  if (path?.learning_path?.course_code) level.value = path.learning_path.course_code
  await loadHistory(true)
  document.addEventListener('click', onDocClick)
})
onBeforeUnmount(() => { stop(); document.removeEventListener('click', onDocClick) })
</script>

<template>
  <div><header class="subpage-header"><button class="icon-button" @click="router.back()"><ArrowLeft /></button><div><p class="eyebrow">AI LESEZEIT</p><h1>{{ l.title }}</h1></div></header>
    <div class="story-tabs"><button :class="{ active: tab === 'generate' }" @click="tab='generate'"><Sparkles :size="17"/>{{ l.generateRead }}</button><button :class="{ active: tab === 'history' }" @click="tab='history'"><History :size="17"/>{{ l.history }} <span>{{ stories.length }}</span></button></div>

    <template v-if="tab === 'generate'">
      <section v-if="!story" class="story-start"><Sparkles :size="36"/><h2>{{ l.create }}</h2><p>{{ l.description }}</p><div class="level-tabs compact"><button :class="{active:level==='A1'}" @click="level='A1'">A1</button><button :class="{active:level==='A2'}" @click="level='A2'">A2</button></div><button class="primary-button" :disabled="busy" @click="generate">{{busy?l.writing:l.generate}}</button><p v-if="error" class="form-error">{{error}}</p></section>
      <article v-else class="story-paper"><div class="ai-notice" :title="story.ai_disclaimer"><Sparkles :size="16"/>{{story.notice}}</div><p class="story-meta">{{ story.level }} · {{ formatDate(story.created_at) }}</p><h1 lang="de">{{story.title}}</h1>
        <div class="speech-controls"><button class="pronounce-inline" :disabled="!speechAvailable" @click="pauseOrResume"><Pause v-if="speaking && !paused" :size="18"/><Play v-else :size="18"/>{{ !speaking ? l.read : paused ? l.resume : l.pause }}</button><button v-if="speaking" class="speech-stop" @click="stop"><Square :size="15"/>{{ l.stop }}</button><div class="speed-switch"><button v-for="value in [.75,1,1.15]" :key="value" :class="{active:speed===value}" @click="changeSpeed(value)">{{value}}×</button></div></div>
        <p v-if="!storySegments" class="story-de" lang="de">{{story.story_de}}</p><p v-else class="story-de" lang="de"><template v-for="(seg, si) in storySegments" :key="si"><span v-if="!seg.word">{{ seg.text }}</span><span v-else class="story-word-hl" :class="{ selected: selectedWordPopup?.word.id===seg.word.id }" @click="selectStoryWord(seg.word, $event)">{{ seg.text }}</span></template></p><details><summary>{{ l.translation }}</summary><p v-if="story.story_translation">{{story.story_translation}}</p><p v-else>{{l.oldTranslation}}</p></details><div class="story-words"><button v-for="word in story.words" :key="word.id" class="story-word-chip" :class="{ selected: selectedWordPopup?.word.id===word.id }" @click="askAboutWord(word)">{{word.display}}<small>{{word.meaning || word.chinese}}</small></button></div><div class="story-questions"><h3>{{l.comprehension}}</h3><p class="question-tip">{{l.questionTip}}</p><section v-for="q in story.questions" :key="q.index" class="story-question"><b>{{q.index + 1}}. {{q.question}}</b><div class="question-choices"><button v-for="choice in q.choices" :key="choice" :disabled="!!q.answered || answeringQuestion===q.index" :class="{selected:q.answered?.selected_choice===choice, correct:q.answered && choice===q.answered.expected, wrong:q.answered && q.answered.selected_choice===choice && !q.answered.correct}" @click="answerQuestion(q,choice)">{{choice}}<span v-if="q.answered && choice===q.answered.expected">✓</span><span v-else-if="q.answered && q.answered.selected_choice===choice && !q.answered.correct">×</span></button></div><p v-if="q.answered" :class="['question-result',q.answered.correct?'correct':'wrong']"><b>{{q.answered.correct?l.correct:l.retry}}</b><span v-if="!q.answered.correct">{{l.answer}} {{q.answered.expected}}</span><small v-if="q.answered.evidence">{{l.evidence}} {{q.answered.evidence}}</small></p></section><p v-if="!story.questions?.length" class="empty-state">{{l.oldQuestions}}</p></div><div class="story-actions"><button class="secondary-button" :disabled="busy" @click="regenerate()"><RefreshCcw :size="17"/>{{l.regenerate}}</button><button class="secondary-button" @click="stop();story=null">{{l.newTopic}}</button></div><div v-if="selectedWordPopup" class="word-popup" :style="{ left: selectedWordPopup.x + 'px', top: selectedWordPopup.y + 'px' }"><div class="word-popup-card"><b>{{ selectedWordPopup.word.display || selectedWordPopup.word.german }}</b><p>{{ selectedWordPopup.word.meaning || selectedWordPopup.word.chinese }}</p><small>{{ selectedWordPopup.word.part_of_speech || '' }}</small><div class="word-popup-actions"><button class="popup-ask-teacher" @click="askAboutWord(selectedWordPopup.word)"><Bot :size="14"/>{{l.ask}}</button><button class="popup-close" @click="closeWordPopup"><X :size="16"/></button></div></div></div><p v-if="error" class="form-error">{{error}}</p></article>
    </template>

    <section v-else class="story-history"><div v-if="historyBusy && !stories.length" class="loading-card">{{l.opening}}</div><div v-else-if="!stories.length" class="empty-state story-empty"><History :size="30"/><b>{{l.empty}}</b><span>{{l.emptyDetail}}</span></div><article v-for="item in stories" :key="item.id"><button class="story-history-main" @click="openStory(item)"><span class="story-level">{{ item.level }}</span><span><small>{{ formatDate(item.created_at) }}<template v-if="item.chapter_id"> · {{l.currentChapter}}</template></small><b>{{ item.title }}</b><em>{{ item.words.slice(0,5).map(word => word.display).join(' · ') }}</em></span></button><div class="history-actions"><button :title="l.sameTitle" :disabled="busy" @click="regenerate(item)"><RefreshCcw :size="17"/></button><button :title="l.delete" @click="removeStory(item)"><Trash2 :size="17"/></button></div></article><button v-if="nextCursor" class="secondary-button history-more" :disabled="historyBusy" @click="loadHistory()">{{historyBusy?l.loading:l.loadMore}}</button></section>
  </div>
</template>

<style scoped>
/* —— 分段 Tab：栏 = 重玻璃纪律；激活态浮起；补足 44px 触控 —— */
.story-tabs {
  background: var(--glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--glass-blur-strong)) saturate(160%);
  backdrop-filter: blur(var(--glass-blur-strong)) saturate(160%);
  border: 1px solid var(--glass-border);
  box-shadow: var(--glass-highlight), var(--glass-shadow);
}
.story-tabs button { min-height: 44px; padding: 10px 16px; }
.story-tabs button.active {
  background: var(--glass-raised-bg);
  box-shadow: var(--glass-raised-shadow);
}

/* —— 起始卡 / 空态卡：写死的 var(--paper) → 中玻璃卡片 —— */
.story-start,
.story-empty {
  background: var(--glass-bg);
  -webkit-backdrop-filter: blur(var(--glass-blur)) saturate(160%);
  backdrop-filter: blur(var(--glass-blur)) saturate(160%);
  border: 1px solid var(--glass-border);
  box-shadow: var(--glass-highlight), var(--glass-shadow);
}
.story-start { padding: 40px 24px; }
.story-empty { padding: 44px 24px; }

/* —— 阅读卡：中玻璃 + 长文本排版（行高、断词、触控） —— */
.story-paper {
  background: var(--glass-bg);
  -webkit-backdrop-filter: blur(var(--glass-blur)) saturate(160%);
  backdrop-filter: blur(var(--glass-blur)) saturate(160%);
  border: 1px solid var(--glass-border);
  border-left: 8px solid var(--sage-soft); /* 保留书脊式设计 */
  box-shadow: var(--glass-highlight), var(--glass-shadow);
}
.story-paper > h1 { font-size: 26px; line-height: 1.25; }
.story-de {
  font-size: 19px;
  line-height: 1.85;
  overflow-wrap: break-word;
  hyphens: auto; /* 配合 lang="de" 正确断德语长词 */
}
.story-paper details p { line-height: 1.75; }
.story-paper summary { padding-top: 14px; padding-bottom: 14px; } /* ≥44px 触控，保留原生展开三角 */
.story-questions h3 { margin-bottom: 10px; font-size: 17px; }
.question-tip{margin:0 0 12px;color:var(--muted);font-size:13px}
.story-question{display:grid;gap:10px;margin:14px 0;padding:14px;border:1px solid var(--glass-border);border-radius:var(--radius-md);background:var(--glass-bg-inset)}
.question-choices{display:grid;gap:8px}.question-choices button{display:flex;align-items:center;justify-content:space-between;min-height:44px;padding:10px 12px;border:1px solid var(--glass-border);border-radius:12px;background:var(--glass-raised-bg);color:var(--ink);text-align:left}.question-choices button:not(:disabled):hover{border-color:var(--sage);background:var(--sage-soft)}.question-choices button.correct{border-color:var(--sage);background:var(--sage-soft);color:var(--sage-dark)}.question-choices button.wrong{border-color:var(--danger);background:var(--danger-soft);color:var(--danger)}
.question-result{display:grid;gap:4px;margin:0;font-size:14px}.question-result.correct{color:var(--sage-dark)}.question-result.wrong{color:var(--danger)}.question-result small{color:var(--muted);line-height:1.55}

/* —— 语音控制条：触控目标 + 玻璃化；写死 #fff 的速档激活态改浮起玻璃 —— */
.pronounce-inline,
.speech-stop { min-height: 44px; padding: 10px 16px; }
.speed-switch {
  background: var(--glass-bg-inset);
  border: 1px solid var(--glass-border);
}
.speed-switch button { min-width: 44px; min-height: 36px; }
.speed-switch button.active {
  background: var(--glass-raised-bg);
  box-shadow: var(--glass-raised-shadow);
}

/* —— 难度选择：写死 var(--paper)/#fff → 玻璃 inset + on-accent —— */
.level-tabs.compact button {
  min-height: 44px;
  background: var(--glass-bg-inset);
  border-color: var(--glass-border);
}
.level-tabs.compact button.active {
  background: var(--sage);
  border-color: var(--sage);
  color: var(--on-accent);
}

/* —— 历史列表行：长列表行纪律，免 blur 玻璃 inset；操作钮 44px —— */
.story-history > article {
  background: var(--glass-bg-inset);
  border: 1px solid var(--glass-border);
}
.history-actions button { width: 44px; height: 44px; }
.history-actions button:hover { background: var(--glass-bg-inset); }

/* —— 暗色补充：写死浅色的 AI 提示条 / 停止键 —— */
[data-theme="dark"] .ai-notice {
  background: rgba(216, 178, 98, .14);
  color: var(--gold);
}
.story-word-hl{cursor:pointer;color:var(--sage-dark);background:var(--sage-soft);border-radius:3px;padding:0 1px;transition:background .15s}.story-word-hl:hover,.story-word-hl.selected{background:var(--sage);color:#fff}
.story-word-chip{display:inline-flex;align-items:center;gap:4px;padding:4px 9px;border:1px solid var(--line);border-radius:999px;background:var(--paper);color:var(--ink);cursor:pointer}.story-word-chip small{color:var(--muted);font-size:11px}.story-word-chip:hover,.story-word-chip.selected{border-color:var(--sage);background:var(--sage-soft)}
.word-popup{position:fixed;z-index:70;transform:translateX(-50%);pointer-events:none}.word-popup-card{pointer-events:auto;display:grid;gap:3px;min-width:140px;max-width:200px;padding:12px 14px;border-radius:14px;background:var(--paper);box-shadow:0 12px 34px rgba(30,36,28,.24);border:1px solid var(--line)}.word-popup-card b{font-size:15px}.word-popup-card p{font-size:14px;margin:0;color:var(--ink)}.word-popup-card small{font-size:11px;color:var(--muted)}.word-popup-actions{display:flex;align-items:center;gap:8px;margin-top:8px}.popup-ask-teacher{display:inline-flex;align-items:center;gap:4px;padding:6px 11px;border:0;border-radius:9px;background:var(--sage);color:#fff;font-size:12px;font-weight:700;cursor:pointer}.popup-ask-teacher:hover{opacity:.9}.popup-close{width:28px;height:28px;padding:4px;border:0;border-radius:8px;background:var(--paper-soft);color:var(--muted);cursor:pointer}
@media(max-width:420px){.story-paper{padding:22px 16px;border-left-width:5px}.story-paper>h1{font-size:29px}.word-popup{transform:translateX(-50%);max-width:calc(100vw - 24px)}.word-popup-card{max-width:min(200px,calc(100vw - 24px));min-width:0}.story-de{overflow-wrap:anywhere}}
</style>
